import { css } from '@emotion/react';
import React from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'redux/schemas';

import { halfSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { handheld, notHandheld } from 'styles/global_defaults/media-queries';

import NvIcon from 'shared/components/nv-icon';
import { sanitize, SanitizationLevel } from 'froala/helpers/sanitizer';
import { useInPersonEventContext } from '../in-person-event-context';
import { formatDate, useFormatTimezone } from '../in-person-event-utils';

const styles = css`
  box-shadow: 1px 2px 2px 0px rgba(0, 0, 0, 0.1);
  border-radius: ${halfSpacing}px;
  .event-details-row {
    ${handheld(css`
      flex-direction: column;
    `)};
    ${notHandheld(css`
      flex-direction: row;
    `)}
    gap: ${standardSpacing}px;

    .detail-item {
      gap: ${halfSpacing}px;
    }

    .half-width {
      flex: 1;
    }
  }
`;

const InPersonEventCard = () => {
  const { sessionId } = useInPersonEventContext();
  const inPersonEvent = useSelector((state: RootState) => state.models.inPersonEventSessions[sessionId]);
  const formatTimezone = useFormatTimezone();

  if (!inPersonEvent) {
    return null;
  }

  const {
    title,
    description,
    startTime,
    endTime,
    timeZone,
    location,
  } = inPersonEvent;

  const formattedTimezone = timeZone ? formatTimezone(timeZone) : '';

  return (
    <div css={styles} className='border border-gray-5 px-5 py-4 w-100'>
      {title && (
        <div className='text-large-regular bold mb-2'>{title}</div>
      )}

      {description && (
        <div
          className='text-large-body mb-4'
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: sanitize(description, SanitizationLevel.BASIC) }}
        />
      )}

      <div className='d-flex event-details-row'>
        {startTime && (
          <div className='d-flex half-width detail-item'>
            <NvIcon icon='dueday' size='medium' className='text-warning' />
            <div className='d-flex flex-column'>
              <div className='text-regular bold text-nowrap'>
                {`${formatDate(startTime, timeZone)}${endTime ? ' -' : ''}`}
              </div>
              {endTime && (
                <div className='text-regular bold text-nowrap'>
                  {`${formatDate(endTime, timeZone)}`}
                </div>
              )}
              {formattedTimezone && (
                <div className='label text-gray-2 mt-1'>
                  {formattedTimezone}
                </div>
              )}
            </div>
          </div>
        )}

        {location && (
          <div className='d-flex half-width detail-item'>
            <NvIcon icon='location' size='medium' className='text-warning' />
            <div className='text-regular bold'>{location}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InPersonEventCard;
